/** Pure helpers for the Flows screen of the Visitors Desk. */

export type StreamKey = 'channel' | 'landing' | 'next_path' | 'outcome'
export type Stream = Partial<Record<StreamKey, string>>
export const STREAM_KEYS: StreamKey[] = ['channel', 'landing', 'next_path', 'outcome']
/** Channel and landing share the desk's filter pills (`f.*`); the later steps have their own keys. */
const PARAM: Record<StreamKey, string> = { channel: 'f.channel', landing: 'f.landing', next_path: 'flow.next_path', outcome: 'flow.outcome' }
export const MIN_NODE_PX = 18

export function streamFromSearch(sp: URLSearchParams): Stream {
  const out: Stream = {}
  for (const k of STREAM_KEYS) {
    const v = sp.get(PARAM[k])
    if (v) out[k] = v
  }
  return out
}

export function streamToSearch(s: Stream): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of STREAM_KEYS) if (s[k]) out[PARAM[k]] = s[k]!
  return out
}

/** Search params with the stream keys replaced by `s`; everything else stays. */
export function withStream(spKey: string, s: Stream): URLSearchParams {
  const next = new URLSearchParams(spKey)
  for (const k of STREAM_KEYS) next.delete(PARAM[k])
  for (const [k, v] of Object.entries(streamToSearch(s))) next.set(k, v)
  return next
}

/** Click on a block: follow that stream; click it again to let go. */
export function toggleStream(s: Stream, column: StreamKey, key: string): Stream {
  const next = { ...s }
  if (next[column] === key) delete next[column]
  else next[column] = key
  return next
}

/** Block heights for one column, proportional to the biggest block, never under 18 px so the label fits. */
export function nodeHeights(column: (number | { visits: number })[], maxPx: number): number[] {
  const v = column.map((n) => (typeof n === 'number' ? n : n.visits))
  const top = Math.max(0, ...v)
  return v.map((x) => (top > 0 ? Math.max(MIN_NODE_PX, Math.round((x / top) * maxPx)) : MIN_NODE_PX))
}

/** Outbound clicks come from the nightly rollups only (`/flows/outbound` has no live today). */
export const OUTBOUND_TODAY = 'Outbound links are counted overnight.'
export function outboundLive(period: string): boolean {
  return period !== 'today'
}
